import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import ReviewTemplateEditor from '../components/reviews/ReviewTemplateEditor';
import EmptyState from '../components/shared/EmptyState';
import { Star, Plus } from 'lucide-react';
import { cn } from '../utils/cn';

const LANGUAGES = ['nl', 'en', 'fr', 'de'];

export default function ReviewTemplates() {
  const { t } = useTranslation();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api.get('/shops')
      .then((res) => {
        setShops(res.data);
        setShopId(res.data[0]?.id || '');
      })
      .catch(() => setShops([]));
  }, []);

  const fetchTemplates = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const res = await api.get('/review-templates', { params: { shop_id: shopId } });
      setTemplates(res.data.data);
      setActiveLanguage((current) =>
        res.data.data.some((tpl) => tpl.language === current)
          ? current
          : res.data.data[0]?.language || null
      );
    } catch (error) {
      console.error('Failed to fetch review templates:', error);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const current = templates.find((tpl) => tpl.language === activeLanguage);
  const missingLanguages = LANGUAGES.filter((lang) => !templates.some((tpl) => tpl.language === lang));

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await api.put(`/review-templates/${current.id}`, values);
      setMessage({ type: 'success', text: t('review_templates.saved') });
      await fetchTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || t('review_templates.save_failed') });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (language) => {
    setSaving(true);
    try {
      const res = await api.post('/review-templates', {
        shop_id: shopId,
        language,
        subject: t('review_templates.new_subject'),
        body: t('review_templates.new_body'),
      });
      setActiveLanguage(res.data.data.language);
      await fetchTemplates();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await api.delete(`/review-templates/${current.id}`);
    setActiveLanguage(null);
    await fetchTemplates();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('review_templates.title')}</h1>
        {shops.length > 1 && (
          <select
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {shops.map((shop) => <option key={shop.id} value={shop.id}>{shop.name}</option>)}
          </select>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">{t('review_templates.intro')}</p>

      {message && (
        <div className={cn(
          'mb-4 rounded-lg px-4 py-3 text-sm border',
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        )}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm max-w-3xl">
        <div className="px-6 pt-4 flex flex-wrap items-center gap-2 border-b border-gray-200">
          {templates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => setActiveLanguage(tpl.language)}
              className={cn(
                'px-3 py-2 -mb-px text-sm font-medium border-b-2 transition-colors',
                tpl.language === activeLanguage
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
                !tpl.is_active && 'italic opacity-60'
              )}
            >
              {t(`languages.${tpl.language}`, { defaultValue: tpl.language.toUpperCase() })}
            </button>
          ))}
          {missingLanguages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => handleAdd(lang)}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-2 -mb-px text-sm text-gray-400 hover:text-primary-600 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {t(`languages.${lang}`, { defaultValue: lang.toUpperCase() })}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="px-6 py-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : current ? (
          <ReviewTemplateEditor
            key={current.id}
            template={current}
            onSave={handleSave}
            onDelete={handleDelete}
            saving={saving}
          />
        ) : (
          <EmptyState
            icon={Star}
            title={t('review_templates.empty_title')}
            description={t('review_templates.empty_description')}
          />
        )}
      </div>
    </div>
  );
}
